import {
  FACEBOOK_SDK_DEFAULT_LOCALE,
  FACEBOOK_SDK_DEFAULT_VERSION,
  FACEBOOK_SDK_SCRIPT_ID,
  FACEBOOK_SDK_WAIT_TIMEOUT_MS,
} from "../constants/facebookSdk";
import type { FacebookSDK } from "../types/facebookSdk";

export type {
  FacebookLoginOptions,
  FacebookLoginResponse,
  FacebookSDK,
} from "../types/facebookSdk";

let sdkLoadPromise: Promise<FacebookSDK> | null = null;
let initializedSignature: string | null = null;

function getExistingSdkScript(): HTMLScriptElement | null {
  const existing = document.getElementById(FACEBOOK_SDK_SCRIPT_ID);
  return existing instanceof HTMLScriptElement ? existing : null;
}

function ensureSdkScript(locale: string): HTMLScriptElement {
  const existing = getExistingSdkScript();
  if (existing) return existing;

  const script = document.createElement("script");
  script.id = FACEBOOK_SDK_SCRIPT_ID;
  script.src = `https://connect.facebook.net/${locale}/sdk.js`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
  return script;
}

function waitForFacebookGlobal(
  script: HTMLScriptElement,
): Promise<FacebookSDK> {
  if (window.FB) {
    return Promise.resolve(window.FB);
  }

  return new Promise<FacebookSDK>((resolve, reject) => {
    let settled = false;
    const startedAt = Date.now();

    const cleanup = () => {
      script.removeEventListener("error", handleScriptError);
      window.clearInterval(checkIntervalId);
      window.clearTimeout(timeoutId);
    };

    const resolveIfReady = () => {
      if (!window.FB) return false;
      settled = true;
      cleanup();
      resolve(window.FB);
      return true;
    };

    const handleScriptError = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error("Falha ao carregar o SDK do Facebook/Meta."));
    };

    const checkIntervalId = window.setInterval(() => {
      resolveIfReady();
    }, 50);

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      if (resolveIfReady()) return;
      settled = true;
      cleanup();
      reject(
        new Error(
          `SDK do Facebook/Meta não ficou disponível após ${Date.now() - startedAt
          }ms.`,
        ),
      );
    }, FACEBOOK_SDK_WAIT_TIMEOUT_MS);

    script.addEventListener("error", handleScriptError);
    resolveIfReady();
  });
}

export function isTrustedFacebookOrigin(origin: string): boolean {
  try {
    const hostname = new URL(origin).hostname;
    return hostname === "facebook.com" || hostname.endsWith(".facebook.com");
  } catch {
    return false;
  }
}

export function getFacebookSdk(): FacebookSDK | null {
  return window.FB ?? null;
}

export async function loadFacebookSdk(params: {
  appId: string;
  locale?: string;
  version?: string;
}): Promise<FacebookSDK> {
  const {
    appId,
    locale = FACEBOOK_SDK_DEFAULT_LOCALE,
    version = FACEBOOK_SDK_DEFAULT_VERSION,
  } = params;
  const initSignature = `${appId}:${version}`;

  if (window.FB) {
    if (initializedSignature !== initSignature) {
      window.FB.init({
        appId,
        cookie: true,
        xfbml: true,
        version,
      });
      initializedSignature = initSignature;
    }
    return window.FB;
  }

  if (!sdkLoadPromise) {
    sdkLoadPromise = (async () => {
      const script = ensureSdkScript(locale);
      const sdk = await waitForFacebookGlobal(script);
      return sdk;
    })().finally(() => {
      sdkLoadPromise = null;
    });
  }

  const sdk = await sdkLoadPromise;
  if (initializedSignature !== initSignature) {
    sdk.init({
      appId,
      cookie: true,
      xfbml: true,
      version,
    });
    initializedSignature = initSignature;
  }
  return sdk;
}
